import { FC, ReactNode, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionDetailsProps,
  AccordionProps,
  AccordionSummary,
  AccordionSummaryProps,
  Box,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import classNames from 'classnames';
import { v4 } from 'uuid';

export type AccordionItemProps = AccordionProps & {
  AccordionDetailsProps?: AccordionDetailsProps;
  AccordionSummaryProps?: AccordionSummaryProps;
  children: ReactNode;
  dense?: boolean;
  heading: ReactNode;
  subheading?: string;
  summaryAction?: ReactNode;
};

const StyledAccordion = styled(Accordion)`
  background: none;

  &::before {
    display: none;
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  &.dense {
    padding: 0;
  }
`;

export const AccordionItem: FC<AccordionItemProps> = ({
  AccordionDetailsProps: accordionDetailsProps,
  AccordionSummaryProps: accordionSummaryProps,
  children,
  dense,
  heading,
  id: customId,
  subheading,
  summaryAction,
  variant = 'default',
  ...rest
}) => {
  const id = useMemo(() => customId ?? v4(), [customId]);
  const contentId = `${id}-content`;

  return (
    <StyledAccordion
      {...rest}
      disableGutters
      id={id}
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary
        {...accordionSummaryProps}
        aria-controls={contentId}
        className={classNames({ tree: variant === 'tree' })}
      >
        {summaryAction ? (
          <Stack alignItems='center' direction='row' justifyContent='space-between' width={1}>
            <Box>
              <Typography lineHeight={1} variant='inherit'>
                {heading}
              </Typography>

              {subheading && (
                <Typography color='text.secondary' lineHeight={1} variant='caption'>
                  {subheading}
                </Typography>
              )}
            </Box>

            <Box m={-1}>{summaryAction}</Box>
          </Stack>
        ) : (
          heading
        )}
      </AccordionSummary>

      <StyledAccordionDetails
        {...accordionDetailsProps}
        className={classNames(accordionDetailsProps?.className, { dense })}
        id={contentId}
      >
        {children}
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};
